export type DateInput = string | number | Date | null | undefined;

export interface ClientDateTimeOptions extends Intl.DateTimeFormatOptions {
  locale?: string;
  format?: string;
  fallback?: string;
}

export interface RelativeTimeOptions {
  locale?: string;
  numeric?: Intl.RelativeTimeFormatNumeric;
  style?: Intl.RelativeTimeFormatStyle;
  fallback?: string;
  thresholdInMinutes?: number;
}

const DEFAULT_FALLBACK = 'N/A';

function ensureDate(input: DateInput): Date | null {
  if (input === null || input === undefined) {
    return null;
  }

  const date = input instanceof Date ? input : new Date(input);
  return Number.isNaN(date.getTime()) ? null : date;
}

function resolveLocale(locale?: string): string {
  if (locale) {
    return locale;
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

function resolveTimeZone(): string | undefined {
  if (typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function') {
    try {
      return new Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function pad(value: number, length: number = 2): string {
  return value.toString().padStart(length, '0');
}

function applyPattern(date: Date, pattern: string): string {
  const replacements: Record<string, string> = {
    yyyy: date.getFullYear().toString(),
    MM: pad(date.getMonth() + 1),
    dd: pad(date.getDate()),
    HH: pad(date.getHours()),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
  };
  return pattern.replace(/yyyy|MM|dd|HH|mm|ss/g, token => replacements[token] ?? token);
}

export function formatClientDateTime(input: DateInput, options: ClientDateTimeOptions = {}): string {
  const date = ensureDate(input);
  if (!date) {
    return options.fallback ?? DEFAULT_FALLBACK;
  }

  const { locale, format, fallback, ...intlOptions } = options;

  if (format) {
    return applyPattern(date, format);
  }

  const resolvedLocale = resolveLocale(locale);
  const timeZone = intlOptions.timeZone ?? resolveTimeZone();

  try {
    return new Intl.DateTimeFormat(resolvedLocale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...intlOptions,
      timeZone,
    }).format(date);
  } catch {
    return fallback ?? DEFAULT_FALLBACK;
  }
}

export function formatClientDate(input: DateInput, options: ClientDateTimeOptions = {}): string {
  return formatClientDateTime(input, {
    dateStyle: 'medium',
    timeStyle: undefined,
    ...options,
  });
}

export function formatClientTime(input: DateInput, options: ClientDateTimeOptions = {}): string {
  return formatClientDateTime(input, {
    dateStyle: undefined,
    timeStyle: 'short',
    ...options,
  });
}

export function formatRelativeTimeFromNow(input: DateInput, options: RelativeTimeOptions = {}): string {
  const date = ensureDate(input);
  if (!date) {
    return options.fallback ?? DEFAULT_FALLBACK;
  }

  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMinutes = Math.round(diffMs / (60 * 1000));

  if (options.thresholdInMinutes && Math.abs(diffMinutes) < options.thresholdInMinutes) {
    return options.fallback ?? 'just now';
  }

  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['day', 60 * 24],
    ['hour', 60],
    ['minute', 1],
    ['second', 1 / 60],
  ];

  for (const [unit, unitSizeInMinutes] of units) {
    const value = diffMinutes / unitSizeInMinutes;
    if (Math.abs(value) >= 1 || unit === 'second') {
      const formatter = new Intl.RelativeTimeFormat(resolveLocale(options.locale), {
        numeric: options.numeric ?? 'auto',
        style: options.style ?? 'long',
      });
      return formatter.format(Math.round(value), unit);
    }
  }

  return options.fallback ?? DEFAULT_FALLBACK;
}

export function getClientTimeZone(): string | undefined {
  return resolveTimeZone();
}

export function parseDateInput(input: DateInput): Date | null {
  return ensureDate(input);
}
